import { memo, useCallback } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import { getSentence } from './translations';

// Sort options available for meetings
const SORT_OPTIONS = [
  { value: 'id', label: getSentence(149) },
  { value: 'title', label: getSentence(150) },
  { value: 'duration', label: getSentence(151) }
];



/**
 * SortingControls component for managing meeting sort order and direction
 * Can be used independently for both search and regular meeting listing
 */
const SortingControls = memo(({
  orderBy = 'id',
  descending = true,
  onOrderByChange,
  onDescendingChange,
  disabled = false,
  isLoading = false
}) => {
  const handleOrderByChange = useCallback((event) => {
    onOrderByChange?.(event.target.value);
  }, [onOrderByChange]);

  const handleDescendingToggle = useCallback(() => {
    onDescendingChange?.(!descending);
  }, [onDescendingChange, descending]);

  // Generate tooltip text based on current sort type and direction
  const getTooltipText = useCallback(() => {
    
    if (orderBy === 'title') {
      return descending ? getSentence(156) : getSentence(157);
    } else if (orderBy === 'duration') {
      return descending ? getSentence(152) : getSentence(153);
    } else {
      // For date/id sorting
      return descending ? getSentence(154) : getSentence(155);
    }
  }, [orderBy, descending]);

  return (
    <Box sx={{ 
      display: 'flex', 
      gap: 2, 
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 16px',
      backgroundColor: '#f8f9fa',
      borderBottom: '2px solid #dee2e6',
      borderTop: '1px solid #e9ecef',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#495057',
            fontWeight: 600,
            fontSize: '0.8rem',
            letterSpacing: '0.025em'
          }}
        >
          {getSentence(170)}
        </Typography>
        <FormControl sx={{ minWidth: 140 }}>
          <Select
            value={orderBy}
            onChange={handleOrderByChange}
            disabled={disabled}
            variant="outlined"
            size="small"
            sx={{
              color: '#495057',
              backgroundColor: 'white',
              borderRadius: 1.5,
              height: 36,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#dee2e6',
                borderWidth: '1px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--mainColor)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--mainColor)',
                boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
              },
              '& .MuiSelect-select': {
                padding: '6px 10px',
                fontWeight: 500,
                fontSize: '0.875rem',
              },
              '& .MuiSvgIcon-root': {
                color: '#6c757d',
              },
              '&.Mui-disabled': {
                backgroundColor: '#f8f9fa',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e9ecef',
                },
              }
            }}
          >
          {SORT_OPTIONS.map((option) => (
            <MenuItem 
              key={option.value} 
              value={option.value}
              sx={{
                color: '#495057',
                fontWeight: 500,
                padding: '6px 12px',
                fontSize: '0.875rem',
                '&:hover': {
                  backgroundColor: '#f8f9fa',
                  color: 'var(--mainColor)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'var(--mainColor)',
                  color: 'white',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#1976d2',
                  }
                }
              }}
            >
              {option.label}
            </MenuItem>
          ))}
          </Select>
        </FormControl>
      </Box>

      {/* Order Toggle Button - Modern Design */}
      <Tooltip 
        title={getTooltipText()}
        placement="top"
      >
        <IconButton
          onClick={handleDescendingToggle}
          disabled={disabled}
          size="small"
          sx={{
            backgroundColor: 'white',
            color: '#6c757d',
            border: '1px solid #dee2e6',
            borderRadius: 1.5,
            width: 36,
            height: 36,
            transform: descending ? 'scaleY(-1)' : 'scaleY(1)', // Mirror on Y-axis for descending
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: '#f8f9fa',
              borderColor: 'var(--mainColor)',
              transform: descending ? 'scaleY(-1) scale(1.05)' : 'scaleY(1) scale(1.05)',
            },
            '&:active': {
              transform: descending ? 'scaleY(-1) scale(0.95)' : 'scaleY(1) scale(0.95)',
            },
            '&:disabled': {
              backgroundColor: '#f8f9fa',
              color: '#adb5bd',
              borderColor: '#e9ecef',
              transform: 'none',
            }
          }}
        >
          <SortIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
});

SortingControls.displayName = 'SortingControls';

export default SortingControls;